import { useCallback, useMemo } from 'preact/hooks';

import { useIntl } from '@/app/lib/localization/hooks/use-intl';
import { type ShowPromiseResult } from '@/app/types/adsgram';
import { type ErrorData } from '@/app/types/yandex';

import { AD_NETWORK } from '@/shared/api/user/types';
import { showPopup } from '@/shared/helpers/telegram/show-popup';
import { AnalyticsService } from '@/shared/service/analytics/analytics-service';
import { ErrorLogger } from '@/shared/service/logger/ErrorLogger';
import {
  useAppActions,
  useGame,
  useSessionId,
  useUserProfile,
} from '@/shared/stores/app-store/app-store';

import { useObfuscatedAdEvents } from '../../../shared/hooks/use-obfuscated-ad-events';
import { makeAdvertisementEvent } from './lib/make-advertisement-event';
import { useMoneyTagAdapter } from './moneytag-adapter';
import { CustomErrorCode, type UseAdvertisementProps } from './types';
import { useAdsgram } from './use-ads-gram';
import { usePlaydeckAdvertisement } from './use-playdeck-ads';
import { useYandexAds } from './use-yandex-ads';

export const useAdvertisement = (
  source: string,
  {
    onReward,
    onError,
    onSkip,

    withPriority = false,
  }: UseAdvertisementProps & { withPriority?: boolean } = {}
) => {
  const t = useIntl();

  const sessionId = useSessionId();

  const user = useUserProfile();
  // const userAvdNetwork = user?.avdNetwork;

  const game = useGame();

  const { fetchUserProfile } = useAppActions();

  const { sendObfuscatedEvents } = useObfuscatedAdEvents();

  const eventProps = useMemo(
    () => ({ sessionId, profile: user, source, gameKey: game?.key }),
    [game?.key, sessionId, source, user]
  );

  const makeHandleReward = useCallback(
    (avdNetwork: AD_NETWORK) => async (value: any) => {
      try {
        const event = makeAdvertisementEvent(value, {
          ...eventProps,
          adNetwork: avdNetwork,
          eventName: 'view',
        });

        sendObfuscatedEvents();

        AnalyticsService.send(event);

        fetchUserProfile();

        onReward?.();
      } catch (err) {
        showPopup({ message: (err as any).toString() });
      }
    },
    [eventProps, fetchUserProfile, onReward, sendObfuscatedEvents]
  );

  const makeHandleError = useCallback(
    (avdNetwork: AD_NETWORK) =>
      (value: any, error: Error | ErrorData | ShowPromiseResult) => {
        let message = t('Something went wrong');

        if ('text' in error) message = error.text;
        if ('message' in error) message = error.message;
        if ('description' in error) message = error.description;

        if ('code' in error && error.code === CustomErrorCode.NO_AVAILABLE_ADS) {
          const event = makeAdvertisementEvent(value, {
            ...eventProps,
            adNetwork: avdNetwork,
            eventName: 'not_found',
            eventProps: { error: { message } },
          });

          AnalyticsService.send(event);
        } else {
          const event = makeAdvertisementEvent(value, {
            ...eventProps,
            adNetwork: avdNetwork,
            eventName: 'view_failed',
            eventProps: { error: { message } },
          });

          AnalyticsService.send(event);
        }

        if (!withPriority) showPopup({ message });

        onError?.(new Error(message));
      },
    [eventProps, onError, t, withPriority]
  );

  const makeHandleSkip = useCallback(
    (avdNetwork: AD_NETWORK) => (value: any) => {
      const event = makeAdvertisementEvent(value, {
        ...eventProps,
        adNetwork: avdNetwork,
        eventName: 'view_skipped',
      });

      AnalyticsService.send(event);

      onSkip?.();
    },
    [eventProps, onSkip]
  );

  // Обработчики для MT нужно создать отдельно из-за различий в сигнатуре
  const handleMoneyTagReward = useCallback(() => {
    const makeEvent = (value: any) =>
      makeAdvertisementEvent(value, {
        ...eventProps,
        adNetwork: AD_NETWORK.MT,
        eventName: 'view',
      });

    sendObfuscatedEvents();
    AnalyticsService.send(makeEvent(null));
    fetchUserProfile();
    onReward?.();
  }, [eventProps, fetchUserProfile, onReward, sendObfuscatedEvents]);

  const handleMoneyTagError = useCallback(
    (error: Error | ErrorData) => {
      let message = t('Something went wrong');
      if (error instanceof Error) {
        message = error.message;
      } else if ('text' in error) {
        message = error.text;
      }

      const makeEvent = (value: any, eventName: string) =>
        makeAdvertisementEvent(value, {
          ...eventProps,
          adNetwork: AD_NETWORK.MT,
          eventName,
          eventProps: { error: { message } },
        });

      if ('code' in error && error.code === CustomErrorCode.NO_AVAILABLE_ADS) {
        AnalyticsService.send(makeEvent(null, 'not_found'));
      } else {
        AnalyticsService.send(makeEvent(null, 'view_failed'));
      }

      if (!withPriority) showPopup({ message });
      onError?.(new Error(message));
    },
    [eventProps, onError, t, withPriority]
  );

  const handleMoneyTagSkip = useCallback(() => {
    const makeEvent = (value: any) =>
      makeAdvertisementEvent(value, {
        ...eventProps,
        adNetwork: AD_NETWORK.MT,
        eventName: 'view_skipped',
      });

    AnalyticsService.send(makeEvent(null));
    onSkip?.();
  }, [eventProps, onSkip]);

  const showAdsGramAds = useAdsgram({
    onReward: makeHandleReward(AD_NETWORK.AG),
    onError: makeHandleError(AD_NETWORK.AG),
    onSkip: makeHandleSkip(AD_NETWORK.AG),
  });

  const { open: showPlaydeckAds } = usePlaydeckAdvertisement(source, {
    onReward: makeHandleReward(AD_NETWORK.PD),
    onError: makeHandleError(AD_NETWORK.PD),
  });

  const showYandexAds = useYandexAds({
    onReward: makeHandleReward(AD_NETWORK.Y),
    onError: makeHandleError(AD_NETWORK.Y),
    onSkip: makeHandleSkip(AD_NETWORK.Y),
  });

  const { show: showMoneyTagAds } = useMoneyTagAdapter({
    onReward: handleMoneyTagReward,
    onError: handleMoneyTagError,
    onSkip: handleMoneyTagSkip,
  });

  const show = useCallback(
    ({
      avdNetwork,
      value,
      innerProps,
    }: {
      avdNetwork: AD_NETWORK;
      value?: any;
      innerProps?: UseAdvertisementProps;
    }) => {
      const event = makeAdvertisementEvent(value, {
        ...eventProps,
        adNetwork: avdNetwork,
        eventName: 'view_started',
      });

      AnalyticsService.send(event);

      switch (avdNetwork) {
        case AD_NETWORK.AG:
          showAdsGramAds(value, innerProps);
          break;

        case AD_NETWORK.PD:
          showPlaydeckAds(value, innerProps);
          break;

        case AD_NETWORK.Y:
          showYandexAds(value, innerProps);
          break;

        case AD_NETWORK.MT:
          showMoneyTagAds();
          break;

        default:
          ErrorLogger.error('Unsupported type of advertisement', 'use-advertisement.ts', {
            avdNetwork: avdNetwork || 'unknown',
          });

          showPopup({
            message: `Unsupported type of advertisement: ${avdNetwork}`,
          });
          break;
      }
    },
    [eventProps, showAdsGramAds, showPlaydeckAds, showYandexAds, showMoneyTagAds]
  );

  return show;
};
